import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const EnterpriseAI = () => {
  const { t } = useTranslation('enterpriseAI');
  const { language } = useLanguage();

  const sections = [
    {
      title: t('sections.architecture.title'),
      description: t('sections.architecture.description'),
      tag: t('sections.architecture.tag')
    },
    {
      title: t('sections.deployment.title'),
      description: t('sections.deployment.description'),
      tag: t('sections.deployment.tag')
    },
    {
      title: t('sections.security.title'),
      description: t('sections.security.description'),
      tag: t('sections.security.tag')
    },
    {
      title: t('sections.lifecycle.title'),
      description: t('sections.lifecycle.description'),
      tag: t('sections.lifecycle.tag')
    },
    {
      title: t('sections.integration.title'),
      description: t('sections.integration.description'),
      tag: t('sections.integration.tag')
    },
    {
      title: t('sections.governance.title'),
      description: t('sections.governance.description'),
      tag: t('sections.governance.tag')
    }
  ];

  return (
    <div className="enterprise-ai-page">
      <SEO 
        title="Enterprise AI Solutions - AI Servers, On-Premise AI & Scalable Infrastructure"
        description="Enterprise-grade AI infrastructure for mission-critical operations in Malaysia. AI servers, on-premise deployment, hybrid AI, data sovereignty, AI agents, and seamless integration with your existing enterprise systems."
        keywords="enterprise AI solutions Malaysia, AI servers Malaysia, on-premise AI deployment, hybrid AI infrastructure, AI agents enterprise, AI implementation and consultation, mission-critical AI, scalable AI solutions, data sovereignty AI, enterprise AI integration"
        canonicalPath={`/${language}/enterprise-ai`}
      />
      {/* Hero Section */}
      <section className="hero-section enterprise-ai-hero">
        <div className="hero-background">
          <div className="hero-bg-image"></div>
          <div className="gradient-layer"></div>
          <div className="particle-layer"></div>
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-6"></div>
              <div className="tech-element tech-element-7"></div>
              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle1')}<br />
                {t('hero.subtitle2')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - Management Perspective */}
      <section className="enterprise-intro-section">
        <div className="container">
          <div className="enterprise-intro-layout">
            <div className="intro-left">
              <div className="card-tag">{t('intro.tag')}</div>
              <h2 className="enterprise-intro-heading">{t('intro.heading')}</h2>
            </div>
            <div className="intro-right">
              <div className="intro-question-block">
                <div className="question-content">
                  <h3 className="question-title">{t('intro.question1.title')}</h3>
                  <p className="question-answer">
                    {t('intro.question1.answer')}
                  </p>
                </div>
              </div>
              
              <div className="intro-question-block">
                <div className="question-content">
                  <h3 className="question-title">{t('intro.question2.title')}</h3>
                  <p className="question-answer">
                    {t('intro.question2.answer')}
                  </p>
                </div>
              </div>
              
              <div className="intro-question-block">
                <div className="question-content">
                  <h3 className="question-title">{t('intro.question3.title')}</h3>
                  <p className="question-answer">
                    {t('intro.question3.answer')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="intro-guide">
            <div className="guide-content">
              <h2 className="guide-title">{t('intro.guideTitle')}</h2>
              <p className="guide-subtitle">{t('intro.guideSubtitle')}</p>
            </div>
            <div className="scroll-indicator">
              <span className="scroll-arrow">↓</span>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise AI Content */}
      <section className="capabilities-grid-section">
        <div className="container">
          <div className="row g-4 mb-5">
            {sections.map((section, index) => (
              <div key={index} className="col-lg-6">
                <div className="capability-card-modern fade-in" style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
                  <div className="card-tag">{section.tag}</div>
                  <h2 className="capability-title-bold">{section.title}</h2>
                  <div className="card-divider"></div>
                  <p className="capability-desc-text">{section.description}</p>
                  {section.tag === t('sections.governance.tag') && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Link to={`/${language}/ai-governance`} className="capability-read-more">
                        {t('sections.governance.readMore')}
                        <span className="read-more-arrow">→</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Width Enterprise Scale CTA Section */}
      <section className="enterprise-scale-cta-section" style={{
        width: '100%',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
        borderTop: '1px solid rgba(59, 130, 246, 0.2)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
        padding: '5rem 0'
      }}>
        <div className="container">
          <div className="capability-card-modern fade-in" style={{ 
            textAlign: 'center', 
            animationDelay: '0.8s',
            background: 'transparent',
            border: 'none',
            padding: '2rem',
            boxShadow: 'none'
          }}>
            <div className="card-tag">{t('cta.tag')}</div>
            <h2 className="capability-title-bold" style={{ textAlign: 'center' }}>{t('cta.title')}</h2>
            <div className="card-divider" style={{ margin: '0 auto 2rem' }}></div>
            <p className="capability-desc-text" style={{ maxWidth: '900px', margin: '0 auto' }}>
              {t('cta.description')}
            </p>
            <div style={{ marginTop: '3rem', marginBottom: '3rem', display: 'flex', justifyContent: 'center' }}>
              <Link to={`/${language}/contact`} className="hero-cta" style={{ 
                fontSize: '1.25rem',
                padding: '1.5rem 3rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1.5rem',
                border: '2px solid',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                backdropFilter: 'blur(10px)'
              }}>
                <span className="cta-chevrons" style={{ margin: 0 }}>
                  <span>›</span><span>›</span><span>›</span>
                </span>
                {t('cta.button')}
                <span className="cta-chevrons-reverse" style={{ margin: 0 }}>
                  <span>‹</span><span>‹</span><span>‹</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnterpriseAI;
